import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faSitemap } from '@fortawesome/pro-regular-svg-icons';

import AutomationConditionsTable, {
	AutomationConditionsConfig,
	convertAutomationConditionValue
} from '../AutomationConditionsTable/AutomationConditionsTable';
import * as S from './DeliveryCompanyFeasibilitiesForm.styled';

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const defaultColSpan = {
	md: 6
};

const DeliveryCompanyFeasibilitiesForm = NiceModal.create(({ id, modalName, mode }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);
	const { t } = useTranslation();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const validateAtLeastOneValue = (context) => {
		const { valueAsString, valueAsNumber, valueAsBoolean, valueAsTime } = context.parent ?? {};
		return valueAsString || valueAsNumber || valueAsBoolean || valueAsTime;
	};

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					dcSettingsId: yup.string().nullable().required(),
					status: yup.string().nullable().required(),
					dcName: yup.string().nullable().required(),
					actionType: yup.string().nullable().required(),
					conditionGroups: yup.array().of(
						yup.object().shape({
							id: yup.number().nullable(),
							conditions: yup.array().of(
								yup.object().shape({
									condition: yup.string().nullable().required(),
									operator: yup
										.string()
										.nullable()
										.when('condition', ([condition], schema) => {
											const item = AutomationConditionsConfig.find(
												(item) => item.name === condition
											);
											return item?.type ? schema.required() : schema;
										}),
									valueAsString: yup
										.string()
										.nullable()
										.test('at-least-one', t('alert.requiredField'), (_, context) =>
											validateAtLeastOneValue(context)
										),
									valueAsNumber: yup
										.number()
										.nullable()
										.test('at-least-one', t('alert.requiredField'), (_, context) =>
											validateAtLeastOneValue(context)
										),
									valueAsBoolean: yup
										.boolean()
										.nullable()
										.test('at-least-one', t('alert.requiredField'), (_, context) =>
											validateAtLeastOneValue(context)
										),
									valueAsTime: yup
										.string()
										.nullable()
										.test('at-least-one', t('alert.requiredField'), (_, context) =>
											validateAtLeastOneValue(context)
										)
								})
							)
						})
					)
				})
				.required(),
		[]
	);

	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const isAuthorized = useAccessControl();

	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			status: null,
			dcName: null,
			conditionGroups: [
				{
					id: null,
					conditions: [
						{
							condition: null,
							operator: null,
							valueAsString: null,
							valueAsNumber: null,
							valueAsBoolean: null,
							valueAsTime: null
						}
					]
				}
			],
			actionType: null,
			dcSettingsId: null
		}
	});

	const {
		control,
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	const {
		fields: conditionGroupsFields,
		append: addConditionGroup,
		remove: removeConditionGroup
	} = useFieldArray({
		control,
		name: 'conditionGroups'
	});

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const fillFeasibilityFormInEdit = async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(SHIPPING_AUTOMATION.GET_DC_FEASIBILITY_RULE, {
				dcFeasibilityId: id
			});
			const { data } = response;

			const conditionsList = data.conditionGroups?.map((group) => {
				const parsedConditions = group?.conditions?.map?.((item) => {
					const parameter = AutomationConditionsConfig.find(
						(condition) => condition.name === item.conditionType
					);

					return {
						...item,
						...convertAutomationConditionValue(item, parameter),
						condition:
							AutomationConditionsConfig.find(
								(condition) =>
									condition?.nameV1 === item.conditionType || condition?.name === item.conditionType
							)?.name ?? item.condition,
						operator: item.conditionOperator ?? null
					};
				});

				return {
					id: group.id,
					conditions: [...parsedConditions]
				};
			});

			reset({
				dcSettingsId: data.dcSettingsId,
				dcName: data.dcName,
				status: data.status,
				actionType: data.actionType,
				conditionGroups: conditionsList,
				id: data.id ?? undefined
			});
			removeConditionGroup();

			conditionsList.forEach((item) => {
				addConditionGroup({
					id: item.id,
					conditions: item.conditions
				});
			});
		} catch (error) {
			AppLogger.error(error);
			showError({});
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (id) {
			fillFeasibilityFormInEdit();
		}
	}, [id]);

	const onSubmit = (data) => {
		setIsLoading(true);
		const URL = SHIPPING_AUTOMATION.SAVE_FEASIBILITY_RULE;
		axiosPrivate
			.post(URL, {
				...(id && { id }),
				...data,
				conditionGroups: data.conditionGroups?.map((group) => {
					return {
						id: group.id ? group.id : undefined,
						conditions: group?.conditions?.map?.((item) => {
							return {
								id: item.id ? item.id : undefined,
								conditionType: item.condition,
								conditionOperator: item.operator === 'equalCustom' ? 'equals' : item.operator,
								valueAsBoolean: item?.valueAsBoolean ?? undefined,
								valueAsNumber: item?.valueAsNumber ?? undefined,
								valueAsString: item?.valueAsString ?? undefined,
								valueAsTime: item?.valueAsTime ?? undefined
							};
						})
					};
				})
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.DELIVERY_COMPANY_FEASIBILITIES));
					handleCloseModal();
				} else AppLogger.error(response);
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.SA003') {
					showError({
						header: 'nameAlreadyExists',
						message: 'error.SA003'
					});
				} else {
					AppLogger.error(error);
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const handleAddConditionGroup = () => {
		addConditionGroup({
			id: null,
			conditions: [
				{
					condition: null,
					operator: null,
					valueAsString: null,
					valueAsNumber: null,
					valueAsBoolean: null,
					valueAsTime: null
				}
			]
		});
	};

	return (
		<Modal
			fullwidth
			maxWidth="large"
			name={modalName}
			className="modal-feasibilityRule"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={id ? 'editFeasibilityRule' : 'addFeasibilityRule'}
					tooltip={''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{id ? 'editFeasibilityRule' : 'addFeasibilityRule'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="deliveryCompany" isLoading={isLoading} required>
						<AutoComplete
							disabled={isViewMode}
							name="dcSettingsId"
							placeholder="selectOne"
							required
							autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS}
							setValue={(value) => {
								setValue('dcName', value?.Name, validateOptions);
								setValue('dcSettingsId', value?.ID, validateOptions);
							}}
							value={watch('dcSettingsId')}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required>
						<EnumSelect
							disabled={isViewMode}
							valueRemovable={false}
							placeholder="selectOne"
							searchable={false}
							name="status"
							enumName={ENUMS.FEASIBILITY_STATUS}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value, validateOptions);
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="actionType" required>
						<EnumSelect
							disabled={isViewMode}
							searchable={false}
							valueRemovable={false}
							required
							placeholder="selectOne"
							name="actionType"
							enumName={ENUMS.DC_FEASIBILITY_RULE_ACTIONS}
							value={watch(`actionType`)}
							setValue={(value) => {
								setValue(`actionType`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					{conditionGroupsFields?.length > 0 &&
						conditionGroupsFields.map((item, index) => (
							<>
								<S.TableFormItem label={index === 0 && 'feasibility.conditions'} required key={item.id}>
									{conditionGroupsFields.length > 1 && <S.GroupSeperator />}
									<AutomationConditionsTable
										control={control}
										viewMode={isViewMode}
										groupIndex={index}
										controlName={`conditionGroups.${index}.conditions`}
										removeConditionGroup={removeConditionGroup}
										isLoading={isLoading}
										conditionsEnumName={
											isAuthorized('EnterprisePackage')
												? ENUMS.FEASIBILITY_CONDITIONS_ENTERPRISE_V2
												: ENUMS.FEASIBILITY_CONDITIONS_SCALE_V2
										}
										isGroupDeletable={conditionGroupsFields?.length > 1}
										deleteGroup={() => {
											removeConditionGroup(index);
										}}
									/>
								</S.TableFormItem>
								{index + 1 < conditionGroupsFields.length && (
									<Typography variant="p2" color="var(--black-40)">
										or
									</Typography>
								)}
							</>
						))}
					{!isViewMode && (
						<Button
							className="mt-4"
							variant="blackOutline"
							icon={faSitemap}
							label="addConditionGroup"
							onClick={handleAddConditionGroup}
						/>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						label={id ? 'save' : 'button.add'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						loading={isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default DeliveryCompanyFeasibilitiesForm;
